import config from './roles.json';
import { prompts as defaultClinicianPrompts } from '../components/env';

const ROLES = new Set(config.roles);
const TENANT_SCHEMA_BY_ACCOUNT_TYPE = Object.freeze({
  student: 'STUDENT',
  socialworker: 'SOC_WORK',
  other: 'MISC',
  medstudent: 'MEDSTUD',
});
const warnedKeys = new Set();
const isTestEnv =
  typeof process !== 'undefined' &&
  process.env &&
  (process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined);

function warnOnce(key, message) {
  if (isTestEnv) {
    throw new Error(`[roles] ${message}`);
  }
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[roles] ${message}`);
  }
}

export function normalizeRole(role) {
  if (typeof role !== 'string') return null;
  const trimmed = role.trim();
  if (trimmed === '') return null;
  const base = trimmed.includes(',') ? trimmed.split(',').pop().trim() : trimmed;
  return base ? base.toLowerCase() : null;
}

export function isPendingApproval(role) {
  if (typeof role !== 'string') return false;
  return role.startsWith('user,') || role.startsWith('unauthenticated,');
}

export function isAllowedRole(role) {
  if (typeof role !== 'string') return false;
  return config.allowedAppRoles.includes(role.trim());
}

export function isInGroup(role, groupName) {
  const base = normalizeRole(role);
  if (base == null) return false;
  const group = config.groups[groupName];
  if (!group) {
    warnOnce(`group:${groupName}`, `unknown group '${groupName}'`);
    return false;
  }
  return group.includes(base);
}

export const PATIENT_USER_SELECT = [
  'role',
  'name',
  'email',
  'schema',
  'patient_full_name',
  'patient_dob',
  'patient_phone',
  'patient_email',
  'patient_ehr_id',
  'patient_verified_schema',
  'patient_verified_row_id',
  'patient_match_status',
  'patient_verified_at',
].join(', ');

// Roles an admin may hand-assign from the Signups dashboard: every declared
// role except the pending placeholder ('user') and 'patient' (patients are
// approved through the verification/roster flow, never by a role edit).
// Mirrors backend roles.assignable_roles().
const NOT_ASSIGNABLE_ROLES = new Set(['user', 'patient']);
export function assignableSignupRoles() {
  return config.roles.filter((role) => !NOT_ASSIGNABLE_ROLES.has(role));
}

export function isPatientRole(role) {
  return normalizeRole(role) === 'patient';
}

export function isSuperAdminRole(role) {
  return normalizeRole(role) === 'superadmin';
}

export function hasDoctorRolePermissions(role) {
  const base = normalizeRole(role);
  return base === 'doctor' || base === 'superadmin';
}

export function hasClinicalAdminPermissions(role) {
  const base = normalizeRole(role);
  return base === 'doctor' || base === 'superadmin' || base === 'admin';
}

export function isReadOnlyRole(role) {
  return isPatientRole(role);
}

export function resolveSignInAccountType(metadataAccountType, dbRole) {
  const metadataRole = normalizeRole(metadataAccountType);
  const databaseRole = normalizeRole(dbRole);
  // A tenant signup account_type (student/socialworker/other/medstudent) is
  // authoritative over a clinical doctor/admin DB role. A clinic-inherited or
  // default 'doctor'/'admin' role must never promote a tenant user to clinical —
  // doing so routes them onto the public/clinic schema (where they can see every
  // row) and renders the clinical "Patient" labels instead of their own.
  // This restores the pre-"superadmin/patient roles" behavior, where the DB role
  // only overrode the signup account_type when the DB role was itself a tenant
  // role. Deliberate elevated roles (superadmin/patient) still win below.
  if (
    metadataRole &&
    TENANT_SCHEMA_BY_ACCOUNT_TYPE[metadataRole] &&
    (databaseRole === 'doctor' || databaseRole === 'admin')
  ) {
    return metadataRole;
  }
  return databaseRole || metadataRole || 'doctor';
}

export function getTenantSchemaForAccountType(accountType) {
  const base = normalizeRole(accountType);
  return base ? TENANT_SCHEMA_BY_ACCOUNT_TYPE[base] || null : null;
}

export function getActiveSchemaForRole(role, rawSchema) {
  const expectedTenantSchema = getTenantSchemaForAccountType(role);
  if (!expectedTenantSchema) return 'public';

  const schemas = String(rawSchema || '')
    .split(',')
    .map((schema) => schema.trim())
    .filter(Boolean);
  if (schemas.length === 0) return 'public';

  const matchingSchema = schemas.find(
    (schema) => schema.toUpperCase() === expectedTenantSchema,
  );
  if (matchingSchema) return expectedTenantSchema;

  return schemas.find((schema) => schema.toLowerCase() !== 'public') || 'public';
}

function normalizeToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitPatientName(value) {
  const raw = String(value || '').trim();
  if (!raw) return { first: '', last: '' };
  if (raw.includes(',')) {
    const [last, rest] = raw.split(',', 2);
    const first = String(rest || '').trim().split(/\s+/)[0] || '';
    return { first: normalizeToken(first), last: normalizeToken(last) };
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  return {
    first: normalizeToken(parts[0] || ''),
    last: normalizeToken(parts.length > 1 ? parts[parts.length - 1] : ''),
  };
}

function sameIsoDate(left, right) {
  if (!left || !right) return false;
  return String(left).slice(0, 10) === String(right).slice(0, 10);
}

export function canReadOwnPatientRow(userRow, scheduleRow) {
  if (!isPatientRole(userRow?.role)) return true;
  if (userRow?.patient_match_status !== 'verified') return false;

  const signupName = splitPatientName(userRow.patient_full_name || userRow.name);
  const rowName = splitPatientName(scheduleRow.patient_name);
  const nameMatches = Boolean(
    signupName.first && signupName.last && rowName.first && rowName.last &&
    signupName.first === rowName.first && signupName.last === rowName.last
  );
  if (!nameMatches) return false;

  const expectedEhr = normalizeToken(userRow.patient_ehr_id);
  const rowEhr = normalizeToken(scheduleRow.EHR_ID || scheduleRow.ehr_id);
  if (expectedEhr && rowEhr && expectedEhr === rowEhr) {
    // Fast path: same EHR on both sides. Identity confirmed by EHR + name.
    return true;
  }
  // EHR doesn't match (or wasn't set on either side). Fall through to
  // name + DOB identity match — verified patients are matched on identity,
  // not on the exact chart row they happened to be linked to at approval
  // time. The EHR-short-circuit was too strict for cases where a patient
  // was approved against one chart but visits were filed later under a
  // different EHR.
  if (!sameIsoDate(userRow.patient_dob, scheduleRow.dob)) return false;
  return true;
}

export function filterRowsForPatient(userRow, rows) {
  if (!isPatientRole(userRow?.role)) return Array.isArray(rows) ? rows : [];
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => canReadOwnPatientRow(userRow, row));
}

function pageEntry(pageKey) {
  const entry = config.pages[pageKey];
  if (!entry) {
    warnOnce(`page:${pageKey}`, `unknown page '${pageKey}'`);
    return null;
  }
  return entry;
}

export function canAccessPage(role, pageKey) {
  const base = normalizeRole(role);
  if (base == null) return false;
  if (!ROLES.has(base)) warnOnce(`role:${base}`, `unknown role '${base}'`);
  const entry = pageEntry(pageKey);
  if (!entry || !entry.allow) return false;
  return entry.allow.includes(base);
}

export function canAccessComponent(role, key) {
  const base = normalizeRole(role);
  if (base == null) return false;
  if (!ROLES.has(base)) warnOnce(`role:${base}`, `unknown role '${base}'`);
  if (typeof key !== 'string' || !key.includes('.')) {
    warnOnce(`compkey:${key}`, `invalid component key '${key}' (expected 'pageKey.componentKey')`);
    return false;
  }
  const dotIdx = key.indexOf('.');
  const pageKey = key.slice(0, dotIdx);
  const compKey = key.slice(dotIdx + 1);
  const entry = pageEntry(pageKey);
  if (!entry || !entry.components || !entry.components[compKey]) {
    warnOnce(`compkey:${key}`, `unknown component key '${key}'`);
    return false;
  }
  return entry.components[compKey].allow.includes(base);
}

export function getNavbarItems(role) {
  const base = normalizeRole(role);
  if (base == null) return [];
  const items = [];
  for (const [pageKey, entry] of Object.entries(config.pages)) {
    if (!entry.navbar) continue;
    if (!entry.allow || !entry.allow.includes(base)) continue;
    items.push({
      pageKey,
      path: entry.path,
      label: entry.navbar.label,
      order: entry.navbar.order ?? 100,
      kind: entry.navbar.kind ?? 'link',
    });
  }
  items.sort((a, b) => a.order - b.order);
  return items;
}

export function getLabel(role, labelKey) {
  const base = normalizeRole(role);
  const block = config.labels[labelKey];
  if (!block) {
    warnOnce(`label:${labelKey}`, `unknown label '${labelKey}'`);
    return undefined;
  }
  if (base && base in block) return block[base];
  return block.default;
}

export function pageKeyForPath(pathname) {
  if (typeof pathname !== 'string') return null;
  for (const [pageKey, entry] of Object.entries(config.pages)) {
    const patterns = [entry.path, ...(entry.altPaths || [])].filter(Boolean);
    for (const pattern of patterns) {
      if (matchesPattern(pattern, pathname)) return pageKey;
    }
  }
  return null;
}

function matchesPattern(pattern, pathname) {
  const patSegs = pattern.split('/').filter(s => s !== '');
  const pathSegs = pathname.split('/').filter(s => s !== '');
  let pIdx = 0;
  let aIdx = 0;
  while (pIdx < patSegs.length) {
    const seg = patSegs[pIdx];
    const optional = seg.endsWith('?');
    if (aIdx >= pathSegs.length) {
      if (!optional) return false;
      pIdx++;
      continue;
    }
    if (seg.startsWith(':')) {
      pIdx++;
      aIdx++;
      continue;
    }
    if (seg !== pathSegs[aIdx]) return false;
    pIdx++;
    aIdx++;
  }
  return aIdx === pathSegs.length;
}

export function getScheduleField(role, fieldKey) {
  const base = normalizeRole(role);
  const block = config.scheduleFields[fieldKey];
  if (!block) {
    warnOnce(`schedFld:${fieldKey}`, `unknown scheduleFields key '${fieldKey}'`);
    return undefined;
  }
  if (base && base in block) return block[base];
  if ('default' in block) return block.default;
  if (fieldKey === 'prompts') return defaultClinicianPrompts;
  return undefined;
}

export function getColumnLabels(role) {
  const base = normalizeRole(role);
  const saveTarget = getLabel(role, 'saveTarget');
  const phys = getLabel(role, 'physicianField');
  const id = getLabel(role, 'idField');
  const entity = getLabel(role, 'entity');
  if (base === 'student') {
    return {
      patient_name: `${saveTarget} Name`,
      class_name: `${saveTarget} Name`,
      physician_name: phys,
      professor_name: phys,
      type: `${entity} Type`,
      email: 'Email',
      EHR_ID: id,
      room_number: 'Room Number',
    };
  }
  if (base === 'socialworker' || base === 'other') {
    return {
      patient_name: `${saveTarget} Name`,
      client_name: `${saveTarget} Name`,
      physician_name: phys,
      case_worker: phys,
      type: `${entity} Type`,
      email: 'Email',
      EHR_ID: id,
      location: 'Location',
    };
  }
  return {
    patient_name: 'Patient Name',
    physician_name: 'Physician Name',
    dob: 'Date of Birth',
    type: 'Type',
    tel_num: 'Cell Phone',
    home_phone: 'Home Phone',
    email: 'Email',
    EHR_ID: 'EHR ID',
  };
}
